// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Play and Spell Intervals
//
// First play then click letters & accidentals to create an interval.

// Interval name type options:
//    longNames: Perfect 4th
//    genericNames: 4th
//    halfWholeNames: Chromatic Half Step, Whole Step, minor third, etc...
//    ascendingDescending: ascending P4
//      The default is: (intervalname) + above/below.
import InteractivePrototype from './interactivePrototype.ts';
import * as Skills from '../uTheory.Skills/skills.ts';
import SkillCalculator from '../uTheory.Skills/skillCalculator.ts';
import * as Notation from '../notation.ts';
import utils from '../utils.ts';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';

class PlayAndWriteIntervals extends InteractivePrototype {
  constructor(displayDiv, options) {
    super();
    this.targetSpeedFromSkills = [
      'ascending_major_perfect',
      'ascending_others',
      'descending_minor_perfect',
      'descending_others',
    ];
    this.settings = {
      instructions: {
        ariaLive: 'alert',
        ariaLiveRegion: 0,
      },
      piano: {},
      editor: {
        fixedWidth: 180,
        ariaLive: 'polite',
        ariaTitle: {
          keySignature: false,
          timeSignature: false,
          barline: false,
          rhythm: false,
        },
      },
      letters: { letterButtons: false, accidentalButtons: true, checkButton: 'Check' },
      checkMark: {
        rightAnswerLength: 1,
        rightAnswerInOutRatio: 0.7,
        wrongAnswerLength: 1.0,
        wrongAnswerInOutRatio: 0.3,
      },
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };

    this.init(displayDiv, options);
  }

  init(displayDiv, options) {
    // copy our settings onto the prototype settings:
    utils.merge(this.settings.piano, {
      lowestNote: options.lowestNote,
      highestNote: options.highestNote,
    });

    // now call the prototype's init function:
    super.init(displayDiv, options).then(() => {
      // Put the piano after everything else:
      // this.displayDiv.removeChild(this.pianoDiv);
      // this.displayDiv.appendChild(this.pianoDiv);
      this.lettersDiv.style.marginBottom = '10px';
    });
  }

  // // Move the piano div above the staff div
  // createCustomDivs() {
  //   this.displayDiv.removeChild(this.pianoDiv);
  // }

  generateQuestions() {
    if (this.optionsTrue('questionPrompt')) {
      this.questionPrompt = this.options.questionPrompt;
    } else {
      this.questionPrompt =
        'Play the note<span style="font-size: 2em;"><br />{question} the given note</span><br />and write both notes on the staff.';
    }
    this.questions = [];

    let questions = [];

    // Either generate or calculate questions:
    if (typeof this.options.drill !== 'undefined' && this.options.drill === true) {
      questions = this.randomQuestionGenerator();
    } else {
      questions = this.prepareManualQuestions();
    }

    this.questionPrompt += ` #{question} of ${questions.length}`;

    questions.forEach((question, questionNumber) => {
      const text = [this.questionText(question.interval), questionNumber + 1];
      const skills = this.calculateSkill(question.interval);

      const note = question.note;
      const note2 = question.note.getNoteAt(question.interval);
      let clef = note.prefersClef();
      clef = clef.filter((aClef) => note2.prefersClef().indexOf(aClef) !== -1);
      clef = clef[Math.floor(Math.random() * clef.length)];

      const questionObject = {
        note: question.note,
        interval: question.interval,
        text,
        skills,
        clef,
      };

      this.questions.push(questionObject);
    }, this);

    this.note = new Notation.Note();
    this.maxQuestions = this.questions.length;
  }

  initializeMusic() {
    const clef = new Notation.Clef('treble');
    this.clef = clef;
    const staff = new Notation.Staff(clef);
    this.questionHolder = new Notation.NoteHolder(new Notation.Note(), 'h');
    this.noteHolder = new Notation.NoteHolder('', 'h');
    this.noteHolder.ghostIfEmpty = true;
    const options = { pitch: true, accidental: false, deleteable: false, selectable: true };
    this.noteHolder.editable = new Notation.Editable(this.noteHolder, options);
    const measure = new Notation.Measure(this.questionHolder, this.noteHolder);

    const voice = new Notation.Voice(measure);
    voice.setStaff(staff);

    const keySig = new Notation.KeySignature('C', 'Major');
    this.music = new Notation.Music(staff, voice, keySig);
  }

  randomQuestionGenerator() {
    const options = { lowestNote: 'C2', highestNote: 'C6' };

    const generator = new QuestionGenerator();
    return generator.multipleSkillsGenerate(
      this.options.skills,
      this.options.maxQuestions,
      options
    );
  }

  prepareManualQuestions() {
    const intervalQuestions = [];
    this.options.questions.forEach((question) => {
      const questionParts = question.split('/');
      const note = new Notation.Note(questionParts[0]);
      const interval = new Notation.Interval(questionParts[1]);
      intervalQuestions.push({ note, interval });
    }, this);
    return intervalQuestions;
  }

  calculateSkill(interval) {
    if (typeof this.options.forceSkill !== 'undefined') {
      return [Skills.makeInt(this.options.forceSkill)];
    }
    // otherwise, invoke a skillCalculator:
    if (typeof this.skillCalculator === 'undefined') {
      this.skillCalculator = new SkillCalculator();
    }

    if (
      typeof this.options.halfWholeNames !== 'undefined' &&
      this.options.halfWholeNames === true
    ) {
      return [
        this.skillCalculator.getPianoHalfWholeSkill(interval),
        this.skillCalculator.getStaffHalfWholeSkill(interval),
      ];
    }
    // GCR: Expand logic to other intervals later
    return false;
  }

  questionText(interval) {
    const intervalText = this.intervalText(interval);
    const directionText = this.directionText(interval);
    const ascDsc =
      typeof this.options.ascendingDescending !== 'undefined' &&
      this.options.ascendingDescending === true;

    if (typeof this.options.directionOnly !== 'undefined' && this.options.directionOnly === true) {
      return directionText;
    }

    if (ascDsc) {
      return `${utils.aOrAn(directionText)} ${directionText} ${intervalText}`;
    }
    return `${utils.aOrAn(intervalText)} ${intervalText} <strong>${directionText}</strong>`;
  }

  intervalText(interval) {
    // longNames: Perfect 4th, minor 2nd, Augmented octave
    if (typeof this.options.longNames !== 'undefined' && this.options.longNames === true) {
      return interval.longName();
    }
    if (
      typeof this.options.halfWholeNames !== 'undefined' &&
      this.options.halfWholeNames === true
    ) {
      return interval.longHalfWholeName();
    }
    if (typeof this.options.genericNames !== 'undefined' && this.options.genericNames === true) {
      return interval.longGenericName();
    }
    return interval.quality + interval.genericInterval;
  }

  directionText(interval) {
    if (
      typeof this.options.ascendingDescending !== 'undefined' &&
      this.options.ascendingDescending === true
    ) {
      return interval.direction > 0 ? 'ascending' : 'descending';
    }
    return interval.direction > 0 ? 'above' : 'below';
  }

  newQuestion() {
    this.piano.clearColors();
    this.letterPanel.accidentalName = '';
    this.noteHolder.editable.eventHandler.setAccidental('');
    super.newQuestion();

    this.note = this.currentQuestion.note;
    this.interval = this.currentQuestion.interval;
    this.autoRange();
    this.piano.colorNote(this.currentQuestion.note);
    this.questionHolder.notes[0] = this.currentQuestion.note;
    this.noteHolder.remove(this.noteHolder.notes[0]);
    this.noteHolder.editable.selected = false;
    this.clef.setName(this.currentQuestion.clef);
    this.editor.renderer.render();

    this.wrongAnswerCount = 0;
  }

  autoRange() {
    const { note } = this;
    const ascInterval = this.interval.direction === 1;
    const generic = this.interval.isLessThan('P8') ? 9 : this.interval.genericInterval + 2;

    let lowestPianoNote;
    let highestPianoNote;
    if (ascInterval) {
      lowestPianoNote = note.getNoteAtGeneric(-2);
      highestPianoNote = note.getNoteAtGeneric(generic);
    } else {
      highestPianoNote = note.getNoteAtGeneric(2);
      lowestPianoNote = note.getNoteAtGeneric(-generic);
    }

    this.piano.setBoundaries(lowestPianoNote, highestPianoNote);
  }

  addListeners() {
    this.piano.addNoteOnListener(this.keyPressed, this);
    this.letterPanel.addAccidentalCallback(this.accidental, this);
    this.letterPanel.addCheckCallback(this.check, this);
  }

  accidental(accidental) {
    this.noteHolder.editable.eventHandler.setAccidental(accidental);
    this.clearWarning();
  }

  keyPressed(midiNumber) {
    this.clearWarning();
    this.piano.toggleNote(midiNumber);
  }

  clearWarning() {
    this.checkMark.off();
  }

  check() {
    if (this.rightAnswerDelay) return;
    let answerMidis = this.piano.getHighlightedNotes();
    const answerNote = this.noteHolder.notes[0];

    // Be sure right # of notes are played:
    if (answerMidis.length < 2) {
      this.checkMark.warn('Play both the given & answer notes on the piano.');
      return;
    }
    if (answerMidis.length > 2) {
      this.checkMark.warn(
        "You've played too many notes!<br />Click a note to remove its highlighting."
      );
      return;
    }
    if (this.noteHolder.isEmpty()) {
      this.checkMark.warn('Remember to draw the answer note on the staff, too!');
      return;
    }
    if (this.interval.direction === 1 && answerNote.isLessThan(this.note)) {
      this.checkMark.warn('Careful: write an ascending, not descending interval.');
      return;
    }
    if (this.interval.direction === -1 && answerNote.isGreaterThan(this.note)) {
      this.checkMark.warn('Careful: write a descending, not ascending interval.');
      return;
    }

    // var startNote = this.note;
    const startMidi = this.note.getMidiNumber();

    const goalNote = this.note.getNoteAt(this.interval);
    const goalMidi = goalNote.getMidiNumber();
    this.goalMidi = goalMidi; // for player
    let goalMidis = [startMidi, goalMidi];
    answerMidis.sort();
    goalMidis.sort();

    const rightIntervalOnPiano = goalMidis[1] - goalMidis[0] === answerMidis[1] - answerMidis[0];

    goalMidis = goalMidis.map((num) => num % 12);
    answerMidis = answerMidis.map((num) => num % 12);
    goalMidis.sort();
    answerMidis.sort();
    const rightPianoNotes =
      // eslint-disable-next-line eqeqeq
      goalMidis[0] == answerMidis[0] &&
      // eslint-disable-next-line eqeqeq
      goalMidis[1] == answerMidis[1];

    const rightStaffNote = answerNote.equals(goalNote);

    if (rightStaffNote && rightPianoNotes && rightIntervalOnPiano) {
      this.rightAnswer(1000);
    } else {
      // Be sure staff & piano match:
      const staffMidis = [this.note.getMidiNumber() % 12, answerNote.getMidiNumber() % 12];
      staffMidis.sort();
      // eslint-disable-next-line eqeqeq
      const pianoStaffMatch =
        staffMidis[0] === answerMidis[0] &&
        // eslint-disable-next-line eqeqeq
        staffMidis[1] == answerMidis[1];
      if (!pianoStaffMatch) {
        this.wrongAnswer("Your staff and piano don't match!");
      } else if (!rightIntervalOnPiano) {
        this.wrongAnswer(`Careful, that's not ${this.currentQuestion.text[0]} the given note.`);
      } else this.wrongAnswer();
    }
    /*
    else { // Wrong Answer:
      // on the second wrong answer in a row, give a hint:
      if(this.wrongAnswerCount > 0) {
        var wrongAnswerText = "";
        if(!rightMidi) wrongAnswerText = "Wrong piano note.";
        if(!rightMidi && !rightLetter) wrongAnswerText += "<br />";
        if(!rightLetter) {
          wrongAnswerText +="Wrong ";
          var spacer = "";
          if(goalNote.letter !== answerNote.letter) {
            wrongAnswerText+= "letter";
            spacer = " & ";
          }
          if(goalNote.accidental !== answerNote.accidental) {
            wrongAnswerText+=spacer + "accidental";
          }
          wrongAnswerText += ".";
        }
        this.wrongAnswer(wrongAnswerText);
      } else {
        this.wrongAnswer();
        this.wrongAnswerCount++;
      }
    }
    */
  }

  debugRightAnswer() {
    const goalNote = this.note.getNoteAt(this.interval);
    const goalMidi = goalNote.getMidiNumber();
    this.goalMidi = goalMidi;
    this.rightAnswer();
  }

  gameOver() {
    this.instructionsDiv.innerHTML = 'Nice work!';
    this.instructionsDiv.setAttribute('role', 'alert');
    this.piano.removeListeners();
    delete this.noteHolder.editable;
    this.displayDiv.removeChild(this.lettersDiv);
    super.gameOver();
  }

  playAnswer() {
    this.player.play(this.note.getMidiNumber(), { startTime: 0, stopTime: 0.5 });
    this.player.play(this.goalMidi, { startTime: 0.5, stopTime: 1.25 });
  }
}

export default PlayAndWriteIntervals;
